import type { ActionTree } from 'vuex'
import type { EmailTemplateState } from './types'
import type { RootState } from '@/store/modules/types'
import { getContext } from '@/utils/general'

// Matches AM2's page size so the template grids load the same batches.
export const TEMPLATES_PER_PAGE = 16

export const emailTemplateActions: ActionTree<EmailTemplateState, RootState> = {
  // Library ("catalogue") templates shipped with the platform.
  async FETCH_LIBRARY_TEMPLATES({ commit, rootState }, { skip = 0 }: { skip?: number } = {}) {
    if (!rootState.authModule.account) { return false }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()
    try {
      commit('SET_IS_FETCHING_LIBRARY_TEMPLATES', true)
      const { templates, count } = await $api.beefree.getCatalogueTemplates(promoterOid, {
        top: TEMPLATES_PER_PAGE,
        skip,
      })
      commit('SET_LIBRARY_TEMPLATES', { templates, count, append: skip > 0 })
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch templates from the library' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_LIBRARY_TEMPLATES', false)
    }
  },

  // Templates the promoter saved from their own emails.
  async FETCH_SAVED_TEMPLATES({ commit, rootState }, { skip = 0 }: { skip?: number } = {}) {
    if (!rootState.authModule.account) { return false }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()
    try {
      commit('SET_IS_FETCHING_SAVED_TEMPLATES', true)
      const { templates, templateCount } = await $api.beefree.getTemplates(promoterOid, {
        top: TEMPLATES_PER_PAGE,
        skip,
      })
      commit('SET_SAVED_TEMPLATES', { templates, count: templateCount, append: skip > 0 })
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch your templates' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_SAVED_TEMPLATES', false)
    }
  },

  // `template` is the Beefree design as a JSON string (what AM2 stores).
  async CREATE_SAVED_TEMPLATE({ commit, rootState }, { name, description, template }: {
    name: string
    description: string
    template: string
  }) {
    if (!rootState.authModule.account) { return false }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()
    try {
      const saved = await $api.beefree.addNewTemplate(promoterOid, {
        meta: { name, description, template },
        taskOid: null,
      })
      commit('PREPEND_SAVED_TEMPLATE', saved)
      $arNotification.push({ type: 'success', message: 'Template saved' })
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to save template' })
      return false
    }
  },

  // Server-side render of a design, with the preview-text/footer injected.
  async GENERATE_BEEFREE_HTML({ rootState }, { design, previewHtml, footerHtml }: {
    // The design as stored on the draft: a JSON string.
    design: object | string
    previewHtml: string | null
    footerHtml: string | null
  }): Promise<string | null> {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()
    try {
      const { html } = await $api.beefree.generateHtml(promoterOid, design, previewHtml, footerHtml)
      return html
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to generate the email HTML' })
      return null
    }
  },
}
